/**
 * API utility functions for communicating with the backend.
 */
import { supabase } from '../supabaseClient';
import { slowRequestQueue, glossaryQueue } from './requestQueue';

// On production (marketscan.tech), use same-origin /api so Vercel rewrites proxy to Railway → no CORS.
// Called per-request so we always use current origin (avoids stale bundle using Railway URL).
function getApiBaseUrl() {
  if (typeof window !== 'undefined') {
    const { protocol, hostname, origin } = window.location;
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';

    // For any hosted frontend (Vercel preview/prod, custom domains, etc.), use same-origin /api
    // so the platform rewrite decides which backend to hit per environment.
    if (protocol === 'https:' && !isLocalhost) return '/api';

    if (/^https?:\/\/(www\.)?marketscan\.tech$/i.test(origin)) return '/api';
  }

  const env = process.env.REACT_APP_API_URL;
  if (env && env.trim()) {
    const trimmed = env.trim().replace(/\/$/, '');
    if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('/')) {
      return trimmed;
    }
    console.warn('[API] REACT_APP_API_URL is missing a protocol; assuming https://', trimmed);
    return `https://${trimmed}`;
  }

  return 'http://localhost:5000/api';
}

/**
 * Get the auth token from Supabase session (used for cookie-login and as fallback for API calls).
 */
async function getToken(signal) {
  const sessionPromise = supabase.auth.getSession();
  if (!signal) {
    const { data: { session } } = await sessionPromise;
    return session?.access_token;
  }

  return new Promise((resolve, reject) => {
    const abort = () => {
      const error = new Error('Authentication lookup was cancelled.');
      error.name = 'AbortError';
      reject(error);
    };
    if (signal.aborted) {
      abort();
      return;
    }
    signal.addEventListener('abort', abort, { once: true });
    sessionPromise.then(({ data: { session } }) => {
      signal.removeEventListener('abort', abort);
      resolve(session?.access_token);
    }, (error) => {
      signal.removeEventListener('abort', abort);
      reject(error);
    });
  });
}

function waitForWithAbort(promise, signal, message = 'Request was cancelled.') {
  if (!signal) return promise;
  return new Promise((resolve, reject) => {
    const abort = () => {
      const error = new Error(message);
      error.name = 'AbortError';
      reject(error);
    };
    if (signal.aborted) {
      abort();
      return;
    }
    signal.addEventListener('abort', abort, { once: true });
    Promise.resolve(promise).then((value) => {
      signal.removeEventListener('abort', abort);
      resolve(value);
    }, (error) => {
      signal.removeEventListener('abort', abort);
      reject(error);
    });
  });
}

/** Default fetch options: send cookies so HTTP-only session is used. */
const defaultFetchOpts = () => ({ credentials: 'include' });

// Payment / mutation operations use a shorter timeout than GET (auth/me needs 35s for cold start;
// POSTs are user-initiated actions that should fail fast and let the user retry).
const POST_TIMEOUT_MS = 30000; // 30s
const SLOW_ENDPOINTS = [
  '/research/job',
  '/competitors/expand',
  '/competitors/search',
  '/competitors/deep-dive',
  '/market/trend-analysis',
  '/seo-analysis',
  '/market-share',
  '/competitors/events',
  '/research/sources',
];

function makeId(prefix = 'req') {
  const randomPart = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2, 11);
  return `${prefix}_${Date.now()}_${randomPart}`;
}

function getClientSessionId() {
  if (typeof window === 'undefined') return makeId('ssr');
  const key = 'market_tool_client_session_id';
  let value = null;
  try {
    value = window.sessionStorage.getItem(key);
    if (!value) {
      value = makeId('session');
      window.sessionStorage.setItem(key, value);
    }
  } catch {
    value = makeId('session');
  }
  return value;
}

function makeRequestId(endpoint) {
  const normalized = String(endpoint || 'api').replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '').slice(0, 40);
  return `${getClientSessionId()}_${normalized || 'api'}_${Date.now()}`;
}

function shouldDebugApi(endpoint) {
  if (typeof window === 'undefined') return false;
  return !!(
    window.localStorage?.getItem('DEBUG_API') ||
    window.localStorage?.getItem('DEBUG_REFRESH') ||
    (SLOW_ENDPOINTS.some((prefix) => String(endpoint).startsWith(prefix)) && window.localStorage?.getItem('DEBUG_FEATURE_CALLS'))
  );
}

function addRequestHeaders(headers, requestId) {
  headers['X-Request-Id'] = requestId;
  headers['X-Client-Session-Id'] = getClientSessionId();
}

function addRequestIdToBody(data, requestId) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return data;
  return data.request_id ? data : { ...data, request_id: requestId };
}

function logApiLifecycle(phase, meta) {
  if (!shouldDebugApi(meta.endpoint)) return;
  console.log(`[API:${phase}]`, meta);
}

async function createBadResponseError(response, { endpoint, method, url, requestId }) {
  const contentType = (response.headers.get('content-type') || '').toLowerCase();
  const htmlSnippet = await response.clone().text()
    .then((text) => text.replace(/\s+/g, ' ').slice(0, 180))
    .catch(() => '');

  console.error('[API] returned HTML instead of JSON:', {
    method,
    endpoint,
    url,
    responseUrl: response.url,
    status: response.status,
    statusText: response.statusText,
    contentType,
    htmlSnippet,
  });

  const err = new Error('API returned HTML instead of JSON. Ensure /api is proxied to the backend (e.g. Vercel rewrites).');
  err.code = 'BAD_RESPONSE';
  err.status = response.status;
  err.contentType = contentType;
  err.url = url;
  err.responseUrl = response.url;
  err.htmlSnippet = htmlSnippet;
  err.requestId = requestId;
  return err;
}

/**
 * Make a POST request to the API (with timeout to prevent infinite loading).
 * @param {string} endpoint - API path
 * @param {object} data - Request body
 * @param {{ timeout?: number }} [opts] - Optional overrides; timeout defaults to POST_TIMEOUT_MS
 */
async function post(endpoint, data, { timeout = POST_TIMEOUT_MS } = {}) {
  const requestId = data?.request_id || makeRequestId(endpoint);
  const headers = {
    'Content-Type': 'application/json',
  };
  addRequestHeaders(headers, requestId);
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  const url = `${getApiBaseUrl()}${endpoint}`;
  const startedAt = Date.now();
  logApiLifecycle('start', { method: 'POST', endpoint, requestId, timeout });
  let response;
  try {
    const token = await getToken(controller.signal);
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(addRequestIdToBody(data, requestId)),
      signal: controller.signal,
      ...defaultFetchOpts(),
    });
  } catch (err) {
    clearTimeout(timeoutId);
    const isAbort = err?.name === 'AbortError' || (typeof err?.message === 'string' && err.message.toLowerCase().includes('aborted'));
    if (isAbort) {
      console.warn('[API] POST timed out:', endpoint);
      const friendly = new Error('Request timed out. Please check your connection and try again.');
      friendly.code = 'TIMEOUT';
      friendly.requestId = requestId;
      logApiLifecycle('timeout', { method: 'POST', endpoint, requestId, durationMs: Date.now() - startedAt });
      throw friendly;
    }
    err.requestId = err.requestId || requestId;
    logApiLifecycle('error', { method: 'POST', endpoint, requestId, durationMs: Date.now() - startedAt, message: err.message });
    throw err;
  }
  clearTimeout(timeoutId);

  const contentType = (response.headers.get('content-type') || '').toLowerCase();
  if (contentType.includes('text/html')) {
    throw await createBadResponseError(response, { endpoint, method: 'POST', url, requestId });
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      error: 'Unknown error occurred',
    }));
    
    // Special handling for usage limit exceeded
    if (response.status === 429) {
      const err = new Error(error.message || 'Usage limit exceeded');
      err.code = 'USAGE_LIMIT_EXCEEDED';
      err.upgradeUrl = error.upgrade_url;
      err.queriesUsed = error.queries_used;
      err.queriesLimit = error.queries_limit;
      err.requestId = requestId;
      throw err;
    }
    
    // Special handling for auth errors
    if (response.status === 401) {
      const err = new Error(error.message || 'Unauthorized');
      err.code = 'UNAUTHORIZED';
      err.requestId = requestId;
      throw err;
    }

    // Email not verified (e.g. checkout/portal blocked until user confirms email)
    if (response.status === 403 && error.code === 'email_not_verified') {
      const err = new Error(error.message || 'Please verify your email first.');
      err.code = 'EMAIL_NOT_VERIFIED';
      err.requestId = requestId;
      throw err;
    }

    const err = new Error(error.error || error.details || `HTTP error! status: ${response.status}`);
    err.requestId = requestId;
    err.status = response.status;
    logApiLifecycle('error', { method: 'POST', endpoint, requestId, durationMs: Date.now() - startedAt, status: response.status, message: err.message });
    throw err;
  }

  const payload = await response.json();
  logApiLifecycle('done', { method: 'POST', endpoint, requestId, durationMs: Date.now() - startedAt, status: response.status });
  return payload;
}

// Refresh flow: (1) Auth waits INITIAL_SESSION then GET /api/auth/me → backend hits Supabase profiles.
// (2) Dashboard mounts after auth, then after a short stagger GET /api/dashboard/state → backend hits Supabase.
// Staggering avoids both requests hitting a cold backend at once and reduces timeouts.
const FETCH_TIMEOUT_MS = 35000; // 35s so auth/me can complete after backend cold start (e.g. Railway)

/**
 * Make a GET request to the API (with timeout to avoid infinite loading).
 * @param {string} endpoint - API path (e.g. '/auth/me')
 * @param {{ signal?: AbortSignal, timeout?: number, cache?: RequestCache }} [options] - Request controls
 */
async function get(endpoint, options = {}) {
  const requestId = options.requestId || makeRequestId(endpoint);
  const headers = {};
  addRequestHeaders(headers, requestId);
  const timeout = options.timeout ?? FETCH_TIMEOUT_MS;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  const url = `${getApiBaseUrl()}${endpoint}`;
  if (process.env.NODE_ENV === 'development') {
    console.log('[API] GET', endpoint, { requestId, pathname: typeof window !== 'undefined' ? window.location.pathname : '' });
  }
  const startedAt = Date.now();
  logApiLifecycle('start', { method: 'GET', endpoint, requestId, timeout });

  const externalSignal = options.signal;
  let offAbort;
  if (externalSignal) {
    if (externalSignal.aborted) {
      clearTimeout(timeoutId);
      const err = new Error('Request was cancelled.');
      err.code = 'ABORTED';
      throw err;
    }
    offAbort = () => controller.abort();
    externalSignal.addEventListener('abort', offAbort);
  }

  let response;
  try {
    const token = await getToken(controller.signal);
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    response = await fetch(url, {
      headers,
      signal: controller.signal,
      ...defaultFetchOpts(),
      ...(options.cache ? { cache: options.cache } : {}),
    });
  } catch (err) {
    clearTimeout(timeoutId);
    if (externalSignal) externalSignal.removeEventListener('abort', offAbort);
    const isAbort = err?.name === 'AbortError' || (typeof err?.message === 'string' && err.message.toLowerCase().includes('aborted'));
    if (isAbort) {
      console.warn('[API] GET aborted or timed out:', endpoint);
      const friendly = new Error('Request timed out or was cancelled. Please try again.');
      friendly.code = 'TIMEOUT';
      friendly.requestId = requestId;
      logApiLifecycle('timeout', { method: 'GET', endpoint, requestId, durationMs: Date.now() - startedAt });
      throw friendly;
    }
    err.requestId = err.requestId || requestId;
    logApiLifecycle('error', { method: 'GET', endpoint, requestId, durationMs: Date.now() - startedAt, message: err.message });
    throw err;
  }

  try {
  const contentType = (response.headers.get('content-type') || '').toLowerCase();
  if (contentType.includes('text/html')) {
    throw await createBadResponseError(response, { endpoint, method: 'GET', url, requestId });
  }
  if (process.env.NODE_ENV === 'development') {
    console.log('[API] GET', endpoint, '->', response.status, response.statusText);
  }

  if (!response.ok) {
    const error = await waitForWithAbort(response.json(), controller.signal, 'Response parsing was cancelled.').catch(() => ({
      error: 'Unknown error occurred',
    }));
    
    if (response.status === 401) {
      const err = new Error(error.message || 'Unauthorized');
      err.code = 'UNAUTHORIZED';
      err.requestId = requestId;
      throw err;
    }
    
    // 502/503/504 = backend unreachable or overloaded (e.g. Railway cold start)
    if (response.status === 502 || response.status === 503 || response.status === 504) {
      const err = new Error(error.error || error.details || `Service temporarily unavailable (${response.status}). Try again in a moment.`);
      err.code = 'BAD_GATEWAY';
      err.status = response.status;
      err.requestId = requestId;
      throw err;
    }

    const err = new Error(error.error || error.details || `HTTP error! status: ${response.status}`);
    err.requestId = requestId;
    err.status = response.status;
    logApiLifecycle('error', { method: 'GET', endpoint, requestId, durationMs: Date.now() - startedAt, status: response.status, message: err.message });
    throw err;
  }

  const payload = await waitForWithAbort(response.json(), controller.signal, 'Response parsing was cancelled.');
  logApiLifecycle('done', { method: 'GET', endpoint, requestId, durationMs: Date.now() - startedAt, status: response.status });
  return payload;
  } catch (err) {
    const isAbort = err?.name === 'AbortError' || (typeof err?.message === 'string' && err.message.toLowerCase().includes('aborted'));
    if (isAbort) {
      console.warn('[API] GET aborted or timed out while reading response:', endpoint);
      const friendly = new Error('Request timed out or was cancelled. Please try again.');
      friendly.code = 'TIMEOUT';
      friendly.requestId = requestId;
      logApiLifecycle('timeout', { method: 'GET', endpoint, requestId, durationMs: Date.now() - startedAt });
      throw friendly;
    }
    err.requestId = err.requestId || requestId;
    throw err;
  } finally {
    clearTimeout(timeoutId);
    if (externalSignal) externalSignal.removeEventListener('abort', offAbort);
  }
}

/**
 * Make a PUT request to the API (with timeout to prevent infinite loading).
 */
async function put(endpoint, data, { timeout = FETCH_TIMEOUT_MS } = {}) {
  const requestId = data?.request_id || makeRequestId(endpoint);
  const headers = {
    'Content-Type': 'application/json',
  };
  addRequestHeaders(headers, requestId);
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  const url = `${getApiBaseUrl()}${endpoint}`;
  const startedAt = Date.now();
  logApiLifecycle('start', { method: 'PUT', endpoint, requestId, timeout });

  let response;
  try {
    const token = await getToken(controller.signal);
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    response = await fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify(addRequestIdToBody(data, requestId)),
      signal: controller.signal,
      ...defaultFetchOpts(),
    });
  } catch (err) {
    clearTimeout(timeoutId);
    const isAbort = err?.name === 'AbortError' || (typeof err?.message === 'string' && err.message.toLowerCase().includes('aborted'));
    if (isAbort) {
      const friendly = new Error('Request timed out. Please check your connection and try again.');
      friendly.code = 'TIMEOUT';
      friendly.requestId = requestId;
      logApiLifecycle('timeout', { method: 'PUT', endpoint, requestId, durationMs: Date.now() - startedAt });
      throw friendly;
    }
    err.requestId = err.requestId || requestId;
    logApiLifecycle('error', { method: 'PUT', endpoint, requestId, durationMs: Date.now() - startedAt, message: err.message });
    throw err;
  }
  clearTimeout(timeoutId);

  const contentType = (response.headers.get('content-type') || '').toLowerCase();
  if (contentType.includes('text/html')) {
    throw await createBadResponseError(response, { endpoint, method: 'PUT', url, requestId });
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error occurred' }));
    if (response.status === 401) {
      const err = new Error(error.message || 'Unauthorized');
      err.code = 'UNAUTHORIZED';
      err.requestId = requestId;
      throw err;
    }
    if (response.status === 502 || response.status === 503 || response.status === 504) {
      const err = new Error(error.error || error.details || `Service temporarily unavailable (${response.status}). Try again in a moment.`);
      err.code = 'BAD_GATEWAY';
      err.status = response.status;
      err.requestId = requestId;
      throw err;
    }
    const err = new Error(error.error || error.details || `HTTP error! status: ${response.status}`);
    err.requestId = requestId;
    err.status = response.status;
    logApiLifecycle('error', { method: 'PUT', endpoint, requestId, durationMs: Date.now() - startedAt, status: response.status, message: err.message });
    throw err;
  }

  const payload = await response.json();
  logApiLifecycle('done', { method: 'PUT', endpoint, requestId, durationMs: Date.now() - startedAt, status: response.status });
  return payload;
}

/**
 * Set HTTP-only session cookie using Supabase access token.
 * Call after Supabase sign-in so subsequent API requests use the cookie (no token in localStorage).
 */
export async function loginSessionCookie(accessToken) {
  const res = await fetch(`${getApiBaseUrl()}/auth/cookie-login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ access_token: accessToken }),
    ...defaultFetchOpts(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || err.error || 'Cookie login failed');
  }
  return res.json();
}

/**
 * Clear the HTTP-only session cookie. Call with credentials so the cookie is sent and the
 * response Set-Cookie is applied. Must complete before navigation so the browser processes the clear.
 */
export async function logoutSessionCookie() {
  const res = await fetch(`${getApiBaseUrl()}/auth/logout`, {
    method: 'POST',
    ...defaultFetchOpts(),
  });
  if (!res.ok) {
    throw new Error(`Logout failed: ${res.status}`);
  }
}

/**
 * Get current user info from backend (uses session cookie or Bearer).
 */
export async function getCurrentUser() {
  return get('/auth/me');
}

/**
 * Update user profile.
 */
export async function updateUserProfile(updates) {
  return post('/auth/profile', updates);
}

/**
 * Conduct market research for a product.
 */
export async function conductResearch(product, location = 'United States', yearsBack = 5) {
  return post('/research', { product, location, years_back: yearsBack });
}

/**
 * Start a background research job that can survive page refreshes.
 * Uses a client-generated id so retries during cold starts don't create duplicate jobs.
 * Returns { success, job: { id, status, product, location, years_back, created_at, expires_at, progress } }.
 */
export async function startResearchJob(product, location = 'United States', yearsBack = 5, clientJobId) {
  const START_TIMEOUT_MS = 65000;
  const MAX_ATTEMPTS = 2;
  let lastErr;

  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
    try {
      return await post('/research/job', { product, location, years_back: yearsBack, client_job_id: clientJobId }, { timeout: START_TIMEOUT_MS });
    } catch (err) {
      lastErr = err;
      const retryable = err?.code === 'TIMEOUT' || err?.name === 'TypeError';
      if (!retryable || attempt === MAX_ATTEMPTS) break;
      await new Promise((resolve) => setTimeout(resolve, attempt * 1500));
    }
  }

  throw lastErr;
}

/**
 * Fast competitor preview (no usage charge). Fires alongside startResearchJob to populate skeleton UI.
 * Returns { success, competitors: [{name, current_price, currency, tagline}] }. Never throws.
 */
export async function getResearchPreview(product, location = 'United States') {
  try {
    return await post('/research/preview', { product, location }, { timeout: 20000 });
  } catch {
    return { success: true, competitors: [] };
  }
}

/**
 * Get the status of a background research job.
 * Returns { success, job } where job.status is one of: pending, running, complete, error, timeout.
 */
export async function getResearchJob(jobId, options = {}) {
  const res = await get(`/research/job/${jobId}`, { ...options, timeout: 10000, cache: 'no-store' });
  if (typeof window !== 'undefined' && window.localStorage?.getItem('DEBUG_RESEARCH_POLL')) {
    console.log('[ResearchPollAPI] getResearchJob', {
      jobId,
      success: res?.success,
      hasJob: !!res?.job,
      status: res?.job?.status,
      hasResult: !!res?.job?.result,
      progress: res?.job?.progress || null,
    });
  }
  return res;
}

/**
 * Conduct market research with streaming progress.
 * Calls onProgress(step, total, label) as each backend step completes.
 * Returns the same shape as conductResearch: { success, product, location, years_back, data, usage }.
 */
export async function conductResearchStream(product, location = 'United States', yearsBack = 5, onProgress) {
  const token = await getToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  // 150s outer timeout: covers the full stream including Gemini processing time.
  const STREAM_TIMEOUT_MS = 150000;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), STREAM_TIMEOUT_MS);

  let response;
  try {
    response = await fetch(`${getApiBaseUrl()}/research/stream`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ product, location, years_back: yearsBack }),
      signal: controller.signal,
      ...defaultFetchOpts(),
    });
  } catch (err) {
    clearTimeout(timeoutId);
    const isAbort = err?.name === 'AbortError' || (typeof err?.message === 'string' && err.message.toLowerCase().includes('aborted'));
    if (isAbort) {
      const friendly = new Error('Research timed out. Please try again.');
      friendly.code = 'TIMEOUT';
      throw friendly;
    }
    throw err;
  }

  if (!response.ok) {
    clearTimeout(timeoutId);
    if (response.status === 429) {
      const error = await response.json().catch(() => ({}));
      const err = new Error(error.message || 'Usage limit exceeded');
      err.code = 'USAGE_LIMIT_EXCEEDED';
      err.queriesUsed = error.queries_used;
      err.queriesLimit = error.queries_limit;
      throw err;
    }
    if (response.status === 401) {
      const err = new Error('Unauthorized');
      err.code = 'UNAUTHORIZED';
      throw err;
    }
    if (response.status === 403) {
      const error = await response.json().catch(() => ({}));
      if (error.code === 'email_not_verified') {
        const err = new Error(error.message || 'Please verify your email first.');
        err.code = 'EMAIL_NOT_VERIFIED';
        throw err;
      }
    }
    if (response.status === 502 || response.status === 503 || response.status === 504) {
      const err = new Error(`Service temporarily unavailable (${response.status}). Try again in a moment.`);
      err.code = 'BAD_GATEWAY';
      err.status = response.status;
      throw err;
    }
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    const err = new Error(error.error || error.details || 'Research failed');
    err.status = response.status;
    throw err;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let resultPayload = null;
  let errorPayload = null;

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === 'progress' && typeof onProgress === 'function') {
              onProgress(data.step, data.total, data.label);
            } else if (data.type === 'result') {
              resultPayload = data;
            } else if (data.type === 'error') {
              errorPayload = data.error;
            }
          } catch (_) { /* ignore parse errors for ping etc */ }
        }
      }
    }
  } catch (err) {
    clearTimeout(timeoutId);
    const isAbort = err?.name === 'AbortError' || (typeof err?.message === 'string' && err.message.toLowerCase().includes('aborted'));
    if (isAbort) {
      const friendly = new Error('Research timed out. Please try again.');
      friendly.code = 'TIMEOUT';
      throw friendly;
    }
    throw err;
  }

  clearTimeout(timeoutId);

  if (buffer.startsWith('data: ')) {
    try {
      const data = JSON.parse(buffer.slice(6));
      if (data.type === 'result') resultPayload = data;
      else if (data.type === 'error') errorPayload = data.error;
    } catch (_) {}
  }

  if (errorPayload) throw new Error(errorPayload);
  if (!resultPayload) throw new Error('No result from research stream');
  return resultPayload;
}

/**
 * Analyze sentiment for a specific competitor.
 */
export async function analyzeSentiment(competitor, product) {
  return post('/sentiment', { competitor, product });
}

/**
 * Compare competitors at a specific price point.
 */
export async function comparePricePoints(product, price, competitors) {
  return post('/compare', { product, price, competitors });
}

/**
 * Find additional competitors beyond the initial list.
 */
export async function findAdditionalCompetitors(product, location, existingCompetitors) {
  return slowRequestQueue.enqueue(() =>
    post('/competitors/expand', { product, location, existing_competitors: existingCompetitors }, { timeout: 90000 })
  );
}

/**
 * Search for a specific competitor by name.
 */
export async function searchSpecificCompetitor(product, location, competitorName) {
  // Niche competitors can be slow — give the backend up to 60s before timing out
  return post('/competitors/search', { product, location, competitor_name: competitorName }, { timeout: 60000 });
}

/**
 * Generate a detailed deep-dive analysis for a competitor (business summary, strengths/weaknesses, metrics, etc.).
 * @param {string} competitorName - Name of the competitor
 * @param {string} product - Product or service category
 * @param {string} [location] - Market location
 * @param {object} [competitorContext] - Optional existing competitor data to give the model context
 */
export async function getCompetitorDeepDive(competitorName, product, location, competitorContext) {
  const body = { competitor_name: competitorName, product, location };
  if (competitorContext) body.competitor_context = competitorContext;
  // Deep dives involve a detailed Gemini prompt and routinely take 20–60s.
  // Queued so the 90s timeout only starts when the request actually begins processing —
  // not while waiting for other concurrent requests to finish.
  return slowRequestQueue.enqueue(() => post('/competitors/deep-dive', body, { timeout: 90000 }));
}

/**
 * Analyze a specific market trend in depth.
 * Returns a structured breakdown: drivers, opportunities, risks, segment impacts, recommendations, etc.
 * Uses grounding + Gemini so can take 20-60s — uses same generous timeout as deep dives.
 * @param {string} trend - The trend description text
 * @param {string} impact - 'positive' | 'negative' | 'neutral'
 * @param {string} product - Product/market being researched
 * @param {string} location - Market location
 * @param {Array}  allTrends - Full market_trends array for context
 * @param {number} yearsBack - Research depth (years)
 */
export async function getMarketTrendAnalysis(trend, impact, product, location, allTrends = [], yearsBack = 5) {
  // Queued so timeout only starts when the request is actually being processed.
  return slowRequestQueue.enqueue(() => post('/market/trend-analysis', {
    trend,
    impact,
    product,
    location,
    all_trends: allTrends,
    years_back: yearsBack,
  }, { timeout: 90000 }));
}

/**
 * Create embedded Stripe checkout session. Returns { client_secret, session_id }.
 * Throws with message and possibly already_subscribed if user has active subscription.
 * @param {string} [plan='pro'] - Plan id: 'pro' | 'pro_annual' | 'growth'
 */
export async function createCheckoutSession(plan = 'pro') {
  return post('/payments/checkout', { plan });
}

/**
 * Get checkout session status for return page (status, customer_email).
 */
export async function getCheckoutSessionStatus(sessionId) {
  return get(`/payments/session-status?session_id=${encodeURIComponent(sessionId)}`);
}

/**
 * Get current user's transaction history (billing history).
 * @returns {{ success: boolean, transactions: Array<{ amount_cents, currency, status, plan, description, created_at }> }}
 */
export async function getTransactions() {
  return get('/payments/transactions');
}

/**
 * Get current user's single intelligence report orders, with files for each order.
 * @returns {{ success: boolean, report_orders: Array<{ id, status, created_at, updated_at, report_files: Array<{ id, label, file_url, file_type, created_at }> }> }}
 */
export async function getReportOrders() {
  return get('/payments/report-orders');
}

/**
 * Upgrade subscription to a higher plan immediately (prorated charge).
 */
export async function upgradePlan(plan) {
  return post('/payments/upgrade', { plan });
}

/**
 * Cancel subscription at end of billing period (no more charges, access until end).
 */
export async function cancelAtPeriodEnd() {
  return post('/payments/cancel-at-period-end', {});
}

/**
 * Undo a pending end-of-period cancellation. Subscription renews as normal at next billing date.
 */
export async function reactivateSubscription() {
  return post('/payments/reactivate', {});
}

/**
 * Schedule downgrade to take effect at end of current billing period.
 */
export async function scheduleDowngrade(plan) {
  return post('/payments/schedule-downgrade', { plan });
}

/**
 * Create customer portal session.
 */
export async function createCustomerPortal() {
  return post('/payments/portal', {});
}

/**
 * Check API health.
 */
export async function checkHealth() {
  return get('/health');
}

/**
 * Get a concise AI-generated definition for a glossary term.
 * Uses the lightweight Gemini lite model — does not count against research query limits.
 * @param {string} term - The highlighted term to define
 * @param {string} [context] - Surrounding text where the term appeared (up to 300 chars)
 * @returns {{ success: boolean, term: string, definition: string }}
 */
/**
 * Generate Sources & Further Reading for a completed research tab.
 * Uses evidence from the initial research pass to find better citation links.
 * Does NOT count against the user's query usage limit.
 * @param {string} product
 * @param {string} location
 * @param {Array} competitors - Array of competitor objects (uses .name and .website fields)
 * @param {object} researchContext - Compact findings from the current research tab
 */
export async function getResearchSources(product, location, competitors, researchContext = {}) {
  return slowRequestQueue.enqueue(() =>
    post('/research/sources', { product, location, competitors, research_context: researchContext }, { timeout: 90000 })
  );
}

export async function defineGlossaryTerm(term, context) {
  // Queued so rapid addition of multiple terms doesn't flood the lite model.
  // Timeout (30s default) only starts when the request is actually dequeued.
  return glossaryQueue.enqueue(() => post('/glossary/define', { term, context: (context || '').slice(0, 300) }));
}

/**
 * Get dashboard state (projects, activeProjectId) from server – for cross-device sync.
 * @param {AbortSignal} [signal] - Optional; when aborted (e.g. on unmount), the request is cancelled.
 */
export async function getDashboardState(signal) {
  return get('/dashboard/state', signal ? { signal } : {});
}

/**
 * Save dashboard state (projects, activeProjectId) to server – for cross-device sync.
 */
export async function saveDashboardStateToServer(payload) {
  return put('/dashboard/state', payload);
}

/**
 * Analyze SEO landscape for competitors in a market.
 * @param {string} product - Product or service category
 * @param {string} location - Market location
 * @param {Array} competitors - Array of competitor objects
 */
export async function analyzeSEO(product, location, competitors) {
  return slowRequestQueue.enqueue(() => post('/seo-analysis', { product, location, competitors }, { timeout: 90000 }));
}

/**
 * Estimate market share breakdown for a set of competitors over recent years.
 * @param {string} product - Product or service category
 * @param {string} location - Market location
 * @param {Array} competitors - Array of competitor objects with at least a `name` field
 * @param {number} [yearsBack=3] - Number of years of history to estimate (1–10)
 */
export async function analyzeMarketShare(product, location, competitors, yearsBack = 3) {
  return slowRequestQueue.enqueue(() => post('/market-share', { product, location, competitors, years_back: yearsBack }, { timeout: 90000 }));
}

/**
 * Get recent events and headlines for a competitor.
 * @param {string} competitorName - Name of the competitor
 * @param {string} product - Product or service category
 * @param {string} [location] - Market location
 * @param {object} [competitorContext] - Optional existing competitor data for context
 * @param {Array} [existingHeadlines] - Optional list of already-known headline titles to exclude
 * @param {number} [monthsBack=1] - Lookback window in months (1–24). Default 1 month, max 2 years.
 */
export async function getCompetitorEvents(competitorName, product, location, competitorContext, existingHeadlines, monthsBack = 1) {
  const body = { competitor_name: competitorName, product, location };
  if (competitorContext) body.competitor_context = competitorContext;
  if (existingHeadlines) body.existing_headlines = existingHeadlines;
  const m = Math.max(1, Math.min(24, parseInt(monthsBack, 10) || 1));
  body.months_back = m;
  // Events use grounding and can take as long as deep dives.
  // Queued so timeout only starts when the request is actually being processed.
  return slowRequestQueue.enqueue(() => post('/competitors/events', body, { timeout: 90000 }));
}
  
